import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {monitorUrl} from "../services/categoryProfileUrls";
import type {ClauseInput, PriceClause, PriceMonitor} from "../types/monitorTypes";
import {done} from "./notices";

type Answer = {id: number | null; monitor: PriceMonitor};

export function useMonitorQuery(databagId: number, path: string) {
    const url = monitorUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<Answer>(url)).data.monitor,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Clause writes with their notices; edits and removals offer Undo. Each rejects on failure for the form to show. */
export function useMonitorActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const write = async (method: 'post' | 'patch' | 'delete', url: string, data?: unknown) => {
            const res = await queryHttp.request<Answer>({method, url, data});
            qc.setQueryData([monitorUrl(databagId, path)], res.data.monitor);
            return res.data;
        };
        const item = (id: number) => monitorUrl(databagId, path, id);
        return {
            add: async (input: ClauseInput) => {
                const res = await write('post', monitorUrl(databagId, path), input);
                done('Clause added.', () => write('delete', item(res.id!)));
            },
            update: async (clause: PriceClause, input: ClauseInput) => {
                await write('patch', item(clause.id), input);
                const previous: Partial<ClauseInput> = {
                    seriesId: clause.series.id, baseMonth: clause.baseMonth, sharePct: clause.sharePct,
                    revision: clause.revision, nextRevision: clause.nextRevision, capPct: clause.capPct,
                    floorPct: clause.floorPct, triggerPct: clause.triggerPct, announcedPct: clause.announcedPct,
                    note: clause.note,
                };
                done('Clause updated.', () => write('patch', item(clause.id), previous));
            },
            remove: async (clause: PriceClause) => {
                await write('delete', item(clause.id));
                done('Clause removed.', () => write('post', monitorUrl(databagId, path, clause.id, true)));
            },
        };
    }, [qc, databagId, path]);
}
